import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Stack,
  Avatar,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Add,
  Search,
  ViewKanban,
  TableChart,
  Phone,
  Email,
  LocationOn,
  TrendingUp,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../services/api';

const PIPELINE_STAGES = [
  { value: 'new', label: 'New', color: '#2196f3' },
  { value: 'contacted', label: 'Contacted', color: '#ff9800' },
  { value: 'qualified', label: 'Qualified', color: '#9c27b0' },
  { value: 'consultation_scheduled', label: 'Consultation Scheduled', color: '#00bcd4' },
  { value: 'consultation_completed', label: 'Consultation Completed', color: '#4caf50' },
  { value: 'proposal_sent', label: 'Proposal Sent', color: '#673ab7' },
  { value: 'negotiating', label: 'Negotiating', color: '#ff5722' },
  { value: 'contracted', label: 'Contracted', color: '#4caf50' },
  { value: 'lost', label: 'Lost', color: '#f44336' },
];

const URGENCY_CONFIG = {
  urgent: { label: 'Urgent', color: 'error', icon: '🔥' },
  high: { label: 'High', color: 'warning', icon: '⚡' },
  medium: { label: 'Medium', color: 'info', icon: '📌' },
  low: { label: 'Low', color: 'default', icon: '📋' },
};

const Leads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [newLeadDialogOpen, setNewLeadDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [draggedLead, setDraggedLead] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/leads/');
      setLeads(response.data?.leads || response.data || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      showSnackbar('Failed to fetch leads', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleViewLead = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.put(`/leads/${leadId}/status`, { status: newStatus });
      await fetchLeads();
      showSnackbar('Lead status updated successfully');
    } catch (error) {
      console.error('Error updating lead status:', error);
      showSnackbar('Failed to update lead status', 'error');
    }
  };

  const handleDragStart = (lead) => {
    setDraggedLead(lead);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (newStatus) => {
    if (draggedLead && draggedLead.status !== newStatus) {
      await handleStatusChange(draggedLead.id, newStatus);
    }
    setDraggedLead(null);
  };

  const getFilteredLeads = () => {
    return leads.filter((lead) => {
      const matchesSearch =
        !searchQuery ||
        `${lead.first_name} ${lead.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.phone?.includes(searchQuery) ||
        lead.property_address?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
      const matchesUrgency = urgencyFilter === 'all' || lead.urgency === urgencyFilter;

      return matchesSearch && matchesStatus && matchesUrgency;
    });
  };

  const getLeadsByStage = (stage) => {
    return getFilteredLeads().filter((lead) => lead.status === stage);
  };

  const getStatusColor = (status) => {
    const stage = PIPELINE_STAGES.find((s) => s.value === status);
    return stage?.color || '#9e9e9e';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Lead Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setNewLeadDialogOpen(true)}
          size="large"
        >
          New Lead
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {PIPELINE_STAGES.map((stage) => (
                    <MenuItem key={stage.value} value={stage.value}>
                      {stage.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Urgency</InputLabel>
                <Select
                  value={urgencyFilter}
                  label="Urgency"
                  onChange={(e) => setUrgencyFilter(e.target.value)}
                >
                  <MenuItem value="all">All Urgency</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                aria-label="view mode"
              >
                <ToggleButton value="kanban" aria-label="kanban view">
                  <ViewKanban sx={{ mr: 1 }} />
                  Pipeline
                </ToggleButton>
                <ToggleButton value="table" aria-label="table view">
                  <TableChart sx={{ mr: 1 }} />
                  Table
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {viewMode === 'kanban' ? (
        <Box sx={{ overflowX: 'auto', pb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ minWidth: 'max-content' }}>
            {PIPELINE_STAGES.map((stage) => {
              const stageLeads = getLeadsByStage(stage.value);
              return (
                <Box
                  key={stage.value}
                  sx={{
                    minWidth: 320,
                    maxWidth: 320,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    p: 2,
                  }}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(stage.value)}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      {stage.label}
                    </Typography>
                    <Chip
                      label={stageLeads.length}
                      size="small"
                      sx={{
                        bgcolor: stage.color,
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>
                  <Stack spacing={2}>
                    {stageLeads.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onView={handleViewLead}
                        onDragStart={handleDragStart}
                      />
                    ))}
                    {stageLeads.length === 0 && (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                        No leads
                      </Typography>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : (
        <Card>
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Property Address</TableCell>
                    <TableCell>Urgency</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredLeads().length > 0 ? (
                    getFilteredLeads().map((lead) => (
                      <TableRow key={lead.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                              {lead.first_name?.[0]}{lead.last_name?.[0]}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {lead.first_name} {lead.last_name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                            <Email fontSize="small" color="action" />
                            {lead.email}
                          </Typography>
                          {lead.phone && (
                            <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                              <Phone fontSize="small" color="action" />
                              {lead.phone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                            <LocationOn fontSize="small" color="action" />
                            {lead.property_address ? (
                              <>
                                {lead.property_address}
                                {lead.property_city && `, ${lead.property_city}`}
                              </>
                            ) : (
                              '-'
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {lead.urgency ? (
                            <Chip
                              label={URGENCY_CONFIG[lead.urgency]?.label || lead.urgency}
                              color={URGENCY_CONFIG[lead.urgency]?.color || 'default'}
                              size="small"
                              icon={
                                <span style={{ fontSize: '0.875rem' }}>
                                  {URGENCY_CONFIG[lead.urgency]?.icon}
                                </span>
                              }
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {lead.lead_score ? (
                            <Chip
                              label={lead.lead_score}
                              size="small"
                              color={
                                lead.lead_score >= 80
                                  ? 'success'
                                  : lead.lead_score >= 50
                                  ? 'warning'
                                  : 'default'
                              }
                              icon={<TrendingUp fontSize="small" />}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              PIPELINE_STAGES.find((s) => s.value === lead.status)?.label ||
                              lead.status ||
                              'New'
                            }
                            size="small"
                            sx={{
                              bgcolor: getStatusColor(lead.status),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {lead.created_at
                            ? format(new Date(lead.created_at), 'MMM dd, yyyy')
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewLead(lead.id)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" py={4}>
                          No leads found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <NewLeadDialog
        open={newLeadDialogOpen}
        onClose={() => setNewLeadDialogOpen(false)}
        onSuccess={() => {
          fetchLeads();
          setNewLeadDialogOpen(false);
          showSnackbar('Lead created successfully');
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const LeadCard = ({ lead, onView, onDragStart }) => {
  const urgencyConfig = URGENCY_CONFIG[lead.urgency] || URGENCY_CONFIG.medium;

  return (
    <Card
      draggable
      onDragStart={() => onDragStart(lead)}
      sx={{
        cursor: 'move',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
              {lead.first_name?.[0]}{lead.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {lead.first_name} {lead.last_name}
              </Typography>
              {lead.lead_score && (
                <Chip
                  label={`Score: ${lead.lead_score}`}
                  size="small"
                  color={
                    lead.lead_score >= 80
                      ? 'success'
                      : lead.lead_score >= 50
                      ? 'warning'
                      : 'default'
                  }
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>
          <Chip
            label={urgencyConfig.label}
            color={urgencyConfig.color}
            size="small"
            icon={<span style={{ fontSize: '0.875rem' }}>{urgencyConfig.icon}</span>}
          />
        </Box>

        <Stack spacing={0.5} mb={2}>
          {lead.email && (
            <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
              <Email fontSize="small" />
              {lead.email}
            </Typography>
          )}
          {lead.phone && (
            <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
              <Phone fontSize="small" />
              {lead.phone}
            </Typography>
          )}
          {lead.property_address && (
            <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
              <LocationOn fontSize="small" />
              {lead.property_address}
              {lead.property_city && `, ${lead.property_city}`}
            </Typography>
          )}
        </Stack>

        <Button
          fullWidth
          variant="outlined"
          size="small"
          startIcon={<Visibility />}
          onClick={() => onView(lead.id)}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );
};

const NewLeadDialog = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    property_address: '',
    property_city: '',
    property_state: '',
    property_zip: '',
    property_county: '',
    source: '',
    estimated_value: '',
    estimated_items: '',
    urgency: 'medium',
    preferred_contact: 'email',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await api.post('/leads/', formData);
      onSuccess();
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        property_address: '',
        property_city: '',
        property_state: '',
        property_zip: '',
        property_county: '',
        source: '',
        estimated_value: '',
        estimated_items: '',
        urgency: 'medium',
        preferred_contact: 'email',
        message: '',
      });
    } catch (err) {
      console.error('Error creating lead:', err);
      setError(err.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>New Lead</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Property Address"
                name="property_address"
                value={formData.property_address}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                name="property_city"
                value={formData.property_city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                name="property_state"
                value={formData.property_state}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                name="property_zip"
                value={formData.property_zip}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="County"
                name="property_county"
                value={formData.property_county}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Source"
                name="source"
                value={formData.source}
                onChange={handleChange}
                placeholder="e.g., Website, Referral, Advertisement"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Property Value"
                name="estimated_value"
                value={formData.estimated_value}
                onChange={handleChange}
                placeholder="e.g., $500,000"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Estimated Number of Items"
                name="estimated_items"
                type="number"
                value={formData.estimated_items}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Urgency</InputLabel>
                <Select
                  name="urgency"
                  value={formData.urgency}
                  label="Urgency"
                  onChange={handleChange}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Preferred Contact Method</InputLabel>
                <Select
                  name="preferred_contact"
                  value={formData.preferred_contact}
                  label="Preferred Contact Method"
                  onChange={handleChange}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="text">Text</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message / Notes"
                name="message"
                value={formData.message}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Lead'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default Leads;
